'use client';

import { memo, useState } from 'react';
import { Box, Chip, TextField, InputAdornment, Stack, IconButton, Paper } from '@mui/material';
import { Search, Sort, FilterList, TuneRounded } from '@mui/icons-material';
import { themeColors } from '../theme/theme';

export const FilterChips = memo(
  ({
    options,
    value,
    onChange,
    onSearch,
    searchPlaceholder = 'Search...',
    sortOptions = [],
    onSortChange,
    currentSort,
    style,
  }) => {
    const [searchQuery, setSearchQuery] = useState('');

    const handleSearchChange = (e) => {
      const query = e.target.value;
      setSearchQuery(query);
      if (onSearch) {
        onSearch(query);
      }
    };

    return (
      <Box sx={{ mb: 2, ...style }}>
        {/* Search Bar */}
        {onSearch && (
          <TextField
            fullWidth
            size="small"
            value={searchQuery}
            onChange={handleSearchChange}
            placeholder={searchPlaceholder}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ color: '#9e9e9e', fontSize: 20 }} />
                </InputAdornment>
              ),
              sx: {
                borderRadius: 3,
                bgcolor: '#f9fafb',
                border: '1px solid #e5e7eb',
                '& fieldset': {
                  border: 'none',
                },
                '&:hover': {
                  bgcolor: '#f3f4f6',
                  borderColor: '#d1d5db',
                },
                '&.Mui-focused': {
                  bgcolor: 'white',
                  borderColor: themeColors.primaryColor,
                  boxShadow: `0 0 0 3px ${themeColors.primaryColor}15`,
                },
              },
            }}
            sx={{ mb: 2 }}
          />
        )}

        {/* Filters Row */}
        <Paper
          elevation={0}
          sx={{
            p: 1.5,
            bgcolor: '#f9fafb',
            border: '1px solid #e5e7eb',
            borderRadius: 2,
            mb: sortOptions.length > 0 ? 1.5 : 0,
          }}
        >
          <Stack direction="row" spacing={0.5} alignItems="center">
            <TuneRounded sx={{ fontSize: 18, color: '#6b7280', mr: 0.5 }} />
            <Box
              sx={{
                display: 'flex',
                gap: 0.75,
                flexWrap: 'wrap',
                flex: 1,
              }}
            >
              {options.map((option) => {
                const optValue = typeof option === 'string' ? option : option.value;
                const optLabel = typeof option === 'string' ? option : option.label;
                return (
                  <Chip
                    key={optValue}
                    label={optLabel}
                    onClick={() => onChange(optValue)}
                    size="small"
                    sx={{
                      bgcolor: value === optValue ? themeColors.primaryColor : 'white',
                      color: value === optValue ? 'white' : '#374151',
                      fontWeight: value === optValue ? 700 : 600,
                      fontSize: 12,
                      height: 28,
                      borderRadius: 1.5,
                      border: value === optValue ? 'none' : '1px solid #d1d5db',
                      px: 1,
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        bgcolor: value === optValue ? themeColors.primaryColor : '#f3f4f6',
                        transform: 'translateY(-1px)',
                        boxShadow:
                          value === optValue
                            ? '0 2px 8px rgba(0,0,0,0.15)'
                            : '0 1px 3px rgba(0,0,0,0.1)',
                      },
                      '& .MuiChip-icon': {
                        color: value === optValue ? 'white' : themeColors.primaryColor,
                        marginLeft: 1,
                      },
                    }}
                  />
                );
              })}
            </Box>
          </Stack>
        </Paper>

        {/* Sort Options - Separate Row */}
        {sortOptions.length > 0 && onSortChange && (
          <Paper
            elevation={0}
            sx={{
              p: 1,
              bgcolor: '#f9fafb',
              border: '1px solid #e5e7eb',
              borderRadius: 2,
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <Sort sx={{ fontSize: 18, color: '#6b7280' }} />
              <Box
                sx={{
                  display: 'flex',
                  gap: 0.75,
                  flexWrap: 'wrap',
                  flex: 1,
                }}
              >
                {sortOptions.map((option) => (
                  <Chip
                    key={option.value}
                    label={option.label}
                    onClick={() => onSortChange(option.value)}
                    size="small"
                    variant={currentSort === option.value ? 'filled' : 'outlined'}
                    sx={{
                      bgcolor:
                        currentSort === option.value
                          ? `${themeColors.primaryColor}15`
                          : 'transparent',
                      color: currentSort === option.value ? themeColors.primaryColor : '#6b7280',
                      fontWeight: currentSort === option.value ? 700 : 500,
                      fontSize: 11,
                      height: 26,
                      borderRadius: 1.5,
                      borderColor:
                        currentSort === option.value ? themeColors.primaryColor : '#d1d5db',
                      px: 1,
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        bgcolor:
                          currentSort === option.value
                            ? `${themeColors.primaryColor}25`
                            : '#f3f4f6',
                        borderColor: themeColors.primaryColor,
                      },
                    }}
                  />
                ))}
              </Box>
            </Stack>
          </Paper>
        )}
      </Box>
    );
  }
);

FilterChips.displayName = 'FilterChips';
